import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Users, Briefcase, FileText, Crown, Mail, UserPlus, Wallet, Search,
  Check, X, Eye, LogOut, TrendingUp, MessageSquare, Save } from "lucide-react";
import api from "../api";
import { useAuth } from "../context/AuthContext";
import { ALL_LANDINGS } from "../data/seo";
import { CITIES } from "../mock";
import ChatThread from "../components/ChatThread";
import { DocButton } from "../components/DocViewer";

const TABS = ["Families", "Caregivers", "Jobs", "Drafts", "Applications", "Support", "Contact", "SEO"];

export default function AdminDashboard() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("Families");
  const [q, setQ] = useState("");
  const [data, setData] = useState({});

  const loadStats = useCallback(() => { api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);

  const loadTab = useCallback(async (t) => {
    const map = { Families: "/admin/families", Caregivers: "/admin/caregivers", Jobs: "/admin/jobs",
      Drafts: "/admin/jobs?status=pending_payment",
      Applications: "/admin/applications", Contact: "/admin/contact", Support: "/admin/support" };
    if (!map[t]) return;
    try { const { data: d } = await api.get(map[t]); setData((p) => ({ ...p, [t]: d })); } catch (e) {}
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadTab(tab); }, [tab, loadTab]);

  const act = async (url, label) => {
    try { await api.post(url); toast.success(label); loadStats(); loadTab(tab); }
    catch (e) { toast.error("Action failed"); }
  };

  const doLogout = () => { logout(); navigate("/"); };

  const filt = (arr, keys) => (arr || []).filter((x) => !q || keys.some((k) => String(x[k] || "").toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="min-h-screen bg-[#fdfaf7]">
      {/* Admin bar */}
      <div className="bg-white border-b border-black/5">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="font-display text-2xl"><span className="text-[#1f1a1b]">Simply</span><span className="text-[#7c2233]">Sitter</span><span className="text-[#8a8285] text-base ml-2 font-sans">Admin</span></div>
          <button onClick={doLogout} className="inline-flex items-center gap-2 text-[14px] text-[#5a5254] hover:text-[#7c2233]"><LogOut className="w-4 h-4" /> Log out</button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 py-8">
        <h1 className="font-display text-2xl text-[#1f1a1b]">Dashboard</h1>
        <p className="text-[14px] text-[#8a8285]">Overview of all registrations and activity</p>

        {/* Stat cards row 1 */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mt-6">
          <Stat icon={Users} value={stats?.users ?? "—"} label="Users" sub={`${stats?.families ?? 0} families · ${stats?.caregivers ?? 0} caregivers`} />
          <Stat icon={Briefcase} value={stats?.jobs ?? "—"} label="Jobs" />
          <Stat icon={FileText} value={stats?.applications ?? "—"} label="Applications" />
          <Stat icon={Crown} value={stats?.active_subscriptions ?? "—"} label="Active subscriptions" />
          <Stat icon={Mail} value={stats?.contact_requests ?? "—"} label="Contact requests" />
        </div>
        {/* Stat cards row 2 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <Stat icon={UserPlus} tint="green" value={stats?.new_families_month ?? "—"} label="New families this month"
            sub={<span className="inline-flex items-center gap-1 text-green-600"><TrendingUp className="w-3.5 h-3.5" /> this month</span>} />
          <Stat icon={Wallet} tint="amber" value={`$${Number(stats?.revenue_month ?? 0).toFixed(2)}`} label="Revenue / month" sub={`${stats?.active_subscriptions ?? 0} × $15.99`} big />
          <Stat icon={Wallet} value={`$${Number(stats?.revenue_total ?? 0).toFixed(2)}`} label="Revenue total" sub="incl. all payments" big />
        </div>

        {/* Tabs */}
        <div className="mt-8 flex flex-wrap gap-1 bg-white rounded-full border border-black/[0.07] p-1 w-fit">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} className={`px-4 py-1.5 rounded-full text-[14px] font-medium transition-colors ${tab === t ? "bg-[#7c2233] text-white" : "text-[#5a5254] hover:text-[#7c2233]"}`}>{t}</button>
          ))}
        </div>

        {tab !== "SEO" && (
          <div className="mt-4 relative max-w-md">
            <Search className="w-4 h-4 text-[#8a8285] absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email"
              className="w-full h-11 rounded-xl border border-black/10 pl-9 pr-3 text-[15px] outline-none focus:border-[#7c2233]" />
          </div>
        )}

        <div className="mt-5">
          {tab === "Families" && <FamiliesTable rows={filt(data.Families, ["name", "email", "location"])} />}
          {tab === "Caregivers" && <CaregiversTable rows={filt(data.Caregivers, ["name", "email", "location"])} act={act} />}
          {tab === "Jobs" && <JobsTable rows={filt(data.Jobs, ["service", "location", "family_name", "family_email"])} act={act} />}
          {tab === "Drafts" && <DraftsTable rows={filt(data.Drafts, ["service", "location", "family_name", "family_email"])} act={act} />}
          {tab === "Applications" && <AppsTable rows={filt(data.Applications, ["caregiver_name", "caregiver_email"])} />}
          {tab === "Support" && <SupportPanel threads={filt(data.Support, ["user_name", "user_email"])} />}
          {tab === "Contact" && <ContactTable rows={filt(data.Contact, ["name", "email"])} />}
          {tab === "SEO" && <SeoManager />}
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, value, label, sub, tint, big }) {
  const tints = { green: "bg-green-50 text-green-600", amber: "bg-amber-50 text-amber-600" };
  return (
    <div className="rounded-2xl border border-black/[0.07] bg-white p-5">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${tints[tint] || "bg-[#f7e4e6] text-[#7c2233]"}`}><Icon className="w-5 h-5" strokeWidth={1.8} /></div>
      <div className={`font-display ${big ? "text-3xl" : "text-3xl"} text-[#1f1a1b] mt-3`}>{value}</div>
      <div className="text-[14px] text-[#5a5254]">{label}</div>
      {sub && <div className="text-[12px] text-[#8a8285] mt-0.5">{sub}</div>}
    </div>
  );
}

function Shell({ children, empty, rows }) {
  if (!rows) return <div className="text-[#8a8285] text-[14px]">Loading…</div>;
  if (rows.length === 0) return <div className="rounded-2xl border border-black/10 bg-white p-8 text-center text-[#8a8285] text-[14px]">{empty}</div>;
  return <div className="rounded-2xl border border-black/[0.07] bg-white overflow-hidden divide-y divide-black/[0.06]">{children}</div>;
}

function FamiliesTable({ rows }) {
  const [chat, setChat] = React.useState(null);
  return <Shell rows={rows} empty="No families yet.">{rows?.map((r) => (
    <div key={r.id} className="p-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div><p className="font-medium text-[#1f1a1b]">{r.name}</p><p className="text-[13px] text-[#8a8285]">{r.email} · {r.phone} · {r.location}</p></div>
        <div className="flex items-center gap-2">
          <span className={`text-[12px] font-medium px-2.5 py-0.5 rounded-full border ${r.membership_active ? "bg-green-50 text-green-700 border-green-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>{r.membership_active ? "Active" : "Unpaid"}</span>
          <button onClick={() => setChat(chat === r.id ? null : r.id)} data-testid={`admin-message-family-${r.id}`} className="inline-flex items-center gap-1.5 text-[13px] text-white bg-[#7c2233] hover:bg-[#661b29] rounded-lg px-3 py-1.5"><MessageSquare className="w-3.5 h-3.5" /> Message</button>
        </div>
      </div>
      {chat === r.id && (
        <div className="mt-3">
          <p className="text-[12px] text-[#8a8285] mb-1.5">This message reaches the family in their dashboard support chat.</p>
          <ChatThread threadType="support" threadId={r.id} meId="admin" placeholder={`Message ${r.name.split(" ")[0]}…`} />
        </div>
      )}
    </div>
  ))}</Shell>;
}

function CaregiversTable({ rows, act }) {
  const [chat, setChat] = React.useState(null);
  return <Shell rows={rows} empty="No caregivers yet.">{rows?.map((r) => (
    <div key={r.id} className="p-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="font-medium text-[#1f1a1b]">{r.name} <StatusPill s={r.status} />
            {!r.has_documents && <span className="ml-1 text-[12px] font-medium text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">No ID/CV uploaded</span>}
          </p>
          <p className="text-[13px] text-[#8a8285]">{r.email} · {r.phone} · {r.location}</p>
          {r.bio && <p className="text-[13px] text-[#5a5254] mt-1 max-w-xl">{r.bio}</p>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <DocButton url={r.id_doc} label="ID" icon={Eye} testid={`admin-view-id-${r.id}`} className="inline-flex items-center gap-1 text-[13px] text-[#7c2233] border border-[#7c2233]/30 rounded-lg px-2.5 py-1.5 hover:bg-[#faf1f2]" />
          <DocButton url={r.resume} label="Resume" icon={Eye} testid={`admin-view-resume-${r.id}`} className="inline-flex items-center gap-1 text-[13px] text-[#7c2233] border border-[#7c2233]/30 rounded-lg px-2.5 py-1.5 hover:bg-[#faf1f2]" />
          <button onClick={() => setChat(chat === r.id ? null : r.id)} data-testid={`admin-message-caregiver-${r.id}`} className="inline-flex items-center gap-1 text-[13px] text-white bg-[#7c2233] hover:bg-[#661b29] rounded-lg px-2.5 py-1.5"><MessageSquare className="w-3.5 h-3.5" /> Message</button>
          {!r.has_documents && <button onClick={() => act(`/admin/caregivers/${r.id}/remind`, "Reminder email sent — asked them to upload ID & CV")} data-testid={`admin-remind-caregiver-${r.id}`} className="inline-flex items-center gap-1 text-[13px] text-[#b45309] border border-amber-300 bg-amber-50 hover:bg-amber-100 rounded-lg px-2.5 py-1.5"><Mail className="w-3.5 h-3.5" /> Remind to upload</button>}
          {r.status === "pending" && <>
            <button onClick={() => act(`/admin/caregivers/${r.id}/approve`, "Caregiver approved")} className="inline-flex items-center gap-1 text-[13px] text-white bg-[#2e7d32] rounded-lg px-2.5 py-1.5"><Check className="w-3.5 h-3.5" /> Approve</button>
            <button onClick={() => act(`/admin/caregivers/${r.id}/reject`, "Caregiver rejected")} className="inline-flex items-center gap-1 text-[13px] text-[#7c2233] border border-[#7c2233]/30 rounded-lg px-2.5 py-1.5"><X className="w-3.5 h-3.5" /> Reject</button>
          </>}
        </div>
      </div>
      {chat === r.id && (
        <div className="mt-3">
          {!r.has_documents && <p className="text-[12px] text-[#8a8285] mb-1.5">Tip: remind them to upload their ID & CV — without it they can't be approved. The caregiver sees this in their dashboard.</p>}
          <ChatThread threadType="support" threadId={r.id} meId="admin" placeholder={`Message ${r.name.split(" ")[0]}…`} />
        </div>
      )}
    </div>
  ))}</Shell>;
}

function DraftsTable({ rows, act }) {
  return <Shell rows={rows} empty="No draft (unpaid) jobs.">{rows?.map((r) => (
    <div key={r.id} className="p-4 flex items-center justify-between gap-4 flex-wrap">
      <div>
        <p className="font-medium text-[#1f1a1b] capitalize">{r.service} — {r.location} <StatusPill s={r.status} /></p>
        <p className="text-[13px] text-[#8a8285]">{r.family_name} · {r.family_email} · {r.family_phone || "—"}</p>
        {r.details && <p className="text-[13px] text-[#5a5254] mt-1 max-w-xl">{r.details}</p>}
        {r.last_reminder_at && <p className="text-[12px] text-[#8a8285] mt-1">Last reminder: {new Date(r.last_reminder_at).toLocaleString()}</p>}
      </div>
      <button onClick={() => act(`/admin/jobs/${r.id}/remind`, "Reminder email sent to the family")} data-testid={`admin-remind-job-${r.id}`} className="inline-flex items-center gap-1.5 text-[13px] text-white bg-[#7c2233] hover:bg-[#661b29] rounded-lg px-3 py-1.5"><Mail className="w-3.5 h-3.5" /> Send reminder</button>
    </div>
  ))}</Shell>;
}

function JobsTable({ rows, act }) {
  return <Shell rows={rows} empty="No jobs yet.">{rows?.map((r) => (
    <div key={r.id} className="p-4 flex items-center justify-between gap-4 flex-wrap">
      <div>
        <p className="font-medium text-[#1f1a1b] capitalize">{r.service} — {r.location} <StatusPill s={r.status} /></p>
        <p className="text-[13px] text-[#8a8285]">{r.family_name} · {r.family_email}</p>
        {r.details && <p className="text-[13px] text-[#5a5254] mt-1 max-w-xl">{r.details}</p>}
      </div>
      {r.status === "pending_review" && <div className="flex gap-2">
        <button onClick={() => act(`/admin/jobs/${r.id}/approve`, "Job approved — now live")} className="inline-flex items-center gap-1 text-[13px] text-white bg-[#2e7d32] rounded-lg px-2.5 py-1.5"><Check className="w-3.5 h-3.5" /> Approve</button>
        <button onClick={() => act(`/admin/jobs/${r.id}/reject`, "Job rejected")} className="inline-flex items-center gap-1 text-[13px] text-[#7c2233] border border-[#7c2233]/30 rounded-lg px-2.5 py-1.5"><X className="w-3.5 h-3.5" /> Reject</button>
      </div>}
    </div>
  ))}</Shell>;
}

function AppsTable({ rows }) {
  return <Shell rows={rows} empty="No applications yet.">{rows?.map((r) => (
    <div key={r.id} className="p-4"><p className="font-medium text-[#1f1a1b]">{r.caregiver_name}</p>
      <p className="text-[13px] text-[#8a8285]">{r.caregiver_email} · {r.caregiver_phone || "—"}</p>
      {r.message && <p className="text-[13px] text-[#5a5254] mt-1">{r.message}</p>}</div>
  ))}</Shell>;
}

function ContactTable({ rows }) {
  return <Shell rows={rows} empty="No contact requests yet.">{rows?.map((r) => (
    <div key={r.id} className="p-4"><p className="font-medium text-[#1f1a1b]">{r.name} <span className="text-[13px] text-[#8a8285] font-normal">{r.email}</span></p>
      <p className="text-[14px] text-[#5a5254] mt-1">{r.message}</p></div>
  ))}</Shell>;
}

const STATIC_SEO_PAGES = [
  { path: "/", label: "Home" },
  { path: "/cities", label: "Cities" },
  { path: "/jobs", label: "Jobs" },
  { path: "/for-families", label: "For families" },
  { path: "/for-caregivers", label: "For caregivers" },
  { path: "/how-it-works", label: "How it works" },
  { path: "/about", label: "About" },
  { path: "/blog", label: "Blog" },
  { path: "/faq", label: "FAQ" },
  { path: "/privacy", label: "Privacy" },
  { path: "/terms", label: "Terms" },
];

function SeoManager() {
  const [overrides, setOverrides] = React.useState({});
  const [q, setQ] = React.useState("");
  const [savingPath, setSavingPath] = React.useState(null);

  React.useEffect(() => {
    api.get("/admin/seo").then(({ data }) => {
      const map = {};
      (data || []).forEach((d) => { map[d.path] = { title: d.title || "", description: d.description || "" }; });
      setOverrides(map);
    }).catch(() => {});
  }, []);

  const pages = React.useMemo(() => {
    const s = STATIC_SEO_PAGES.slice();
    CITIES.forEach((c) => s.push({ path: `/city/${c.slug}`, label: `City: ${c.name}` }));
    ALL_LANDINGS.forEach((l) => s.push({ path: `/${l.slug}`, label: `${l.service.label} · ${l.city.name}` }));
    return s;
  }, []);

  const filtered = pages.filter((p) => !q || p.path.toLowerCase().includes(q.toLowerCase()) || p.label.toLowerCase().includes(q.toLowerCase()));
  const setField = (path, k, v) => setOverrides((o) => ({ ...o, [path]: { title: "", description: "", ...(o[path] || {}), [k]: v } }));
  const save = async (path) => {
    const o = overrides[path] || { title: "", description: "" };
    setSavingPath(path);
    try { await api.put("/admin/seo", { path, title: o.title, description: o.description }); toast.success(`SEO saved for ${path}`); }
    catch (e) { toast.error("Save failed"); }
    finally { setSavingPath(null); }
  };

  return (
    <div>
      <p className="text-[14px] text-[#5a5254] mb-3">Edit the meta title & description for any page. Leave blank to use the built-in default. {pages.length} pages available.</p>
      <div className="relative max-w-md mb-4">
        <Search className="w-4 h-4 text-[#8a8285] absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a page (e.g. nanny, miami, /about)" data-testid="seo-search"
          className="w-full h-11 rounded-xl border border-black/10 pl-9 pr-3 text-[15px] outline-none focus:border-[#7c2233]" />
      </div>
      <div className="space-y-3">
        {filtered.slice(0, 80).map((p) => {
          const o = overrides[p.path] || { title: "", description: "" };
          return (
            <div key={p.path} className="rounded-2xl border border-black/[0.07] bg-white p-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div><span className="font-medium text-[#1f1a1b] text-[14px]">{p.label}</span> <a href={p.path} target="_blank" rel="noreferrer" className="text-[12px] text-[#7c2233] hover:underline">{p.path}</a></div>
                <button onClick={() => save(p.path)} disabled={savingPath === p.path} data-testid={`seo-save-${p.path}`}
                  className="inline-flex items-center gap-1.5 text-[13px] text-white bg-[#7c2233] hover:bg-[#661b29] disabled:opacity-60 rounded-lg px-3 py-1.5"><Save className="w-3.5 h-3.5" /> Save</button>
              </div>
              <div className="mt-3 space-y-2">
                <div>
                  <label className="text-[12px] text-[#8a8285]">Meta title <span className={o.title.length > 60 ? "text-red-600" : ""}>({o.title.length}/60)</span></label>
                  <input value={o.title} onChange={(e) => setField(p.path, "title", e.target.value)} data-testid={`seo-title-${p.path}`}
                    className="w-full h-10 rounded-lg border border-black/10 px-3 text-[14px] outline-none focus:border-[#7c2233]" placeholder="Custom meta title…" />
                </div>
                <div>
                  <label className="text-[12px] text-[#8a8285]">Meta description <span className={o.description.length > 160 ? "text-red-600" : ""}>({o.description.length}/160)</span></label>
                  <textarea value={o.description} onChange={(e) => setField(p.path, "description", e.target.value)} rows={2} data-testid={`seo-desc-${p.path}`}
                    className="w-full rounded-lg border border-black/10 px-3 py-2 text-[14px] outline-none focus:border-[#7c2233] resize-none" placeholder="Custom meta description…" />
                </div>
              </div>
            </div>
          );
        })}
        {filtered.length > 80 && <p className="text-[13px] text-[#8a8285]">Showing first 80 — refine your search to find more pages.</p>}
      </div>
    </div>
  );
}

function SupportPanel({ threads }) {
  const [active, setActive] = React.useState(null);
  if (!threads) return <div className="text-[#8a8285] text-[14px]">Loading…</div>;
  if (threads.length === 0) return <div className="rounded-2xl border border-black/10 bg-white p-8 text-center text-[#8a8285] text-[14px]">No support messages yet.</div>;
  return (
    <div className="grid md:grid-cols-[1fr_1.3fr] gap-4">
      <div className="rounded-2xl border border-black/[0.07] bg-white overflow-hidden divide-y divide-black/[0.06] h-fit">
        {threads.map((t) => (
          <button key={t.thread_id} onClick={() => setActive(t.thread_id)} className={`w-full text-left p-4 ${active === t.thread_id ? "bg-[#faf1f2]" : "hover:bg-[#fdfaf7]"}`}>
            <p className="font-medium text-[#1f1a1b]">{t.user_name} <span className="text-[11px] font-normal text-[#8a8285] capitalize">· {t.user_role}</span></p>
            <p className="text-[13px] text-[#8a8285]">{t.user_email}</p>
            <p className="text-[13px] text-[#5a5254] mt-1 truncate">{t.messages[t.messages.length - 1]?.body}</p>
          </button>
        ))}
      </div>
      <div>
        {active ? (
          <ChatThread threadType="support" threadId={active} meId="admin" placeholder="Reply to this user…" height="h-96" />
        ) : (
          <div className="rounded-2xl border border-black/10 bg-white p-8 text-center text-[#8a8285] text-[14px]">Select a conversation to reply.</div>
        )}
      </div>
    </div>
  );
}


function StatusPill({ s }) {
  const map = {
    pending: "bg-blue-50 text-blue-700 border-blue-200", approved: "bg-green-50 text-green-700 border-green-200",
    rejected: "bg-red-50 text-red-700 border-red-200", incomplete: "bg-gray-50 text-gray-500 border-gray-200",
    pending_payment: "bg-amber-50 text-amber-700 border-amber-200", pending_review: "bg-blue-50 text-blue-700 border-blue-200",
    live: "bg-green-50 text-green-700 border-green-200",
  };
  const label = { pending_payment: "awaiting payment", pending_review: "in review" }[s] || s;
  return <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border align-middle ${map[s] || "bg-gray-50 text-gray-500 border-gray-200"}`}>{label}</span>;
}
